import {Icon, IconNames} from '@atoms';
import {useAppDispatch, useAppSelector, useFeatureToggle} from '@hooks';
import {CheckInEventType, CheckInSourceType, trackEvent, trackPageComponentedRendered} from '@lib/infrastructure';
import {
  checkInRelevant,
  getDefaultBuilding,
  getIsLoadingUserCheckIn,
  getParkingReservationByDate,
  getUserCheckInStatus,
  updateCheckInStatusNew,
  useHomeTime,
  withAsyncThunkErrorHandling,
} from '@lib/store';
import {timeLabel} from '@lib/store/src/data/timeslots/dateTimeHelpers';
import {
  getActiveWorkday,
  getSomeWorkdaysHaveLoaded,
  getWorkdaysForDate,
  getWorkspaceReservationsForDate,
} from '@lib/store/src/data/timeslots/selectors/index';
import {Button} from '@molecules';
import {LoaderTile, Tile} from '@organisms';
import {FlexCol, FlexRow, H4, Subtitle} from '@quarks';
import {cn} from '@utils';
import {format} from 'date-fns';
import {MouseEvent, useEffect, useState} from 'react';
import toast from 'react-hot-toast';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

interface Props {
  date: Date;
  time: Date;
}

export const WorkdayTile = ({date, time}: Props) => {
  const enabledFeatures = useFeatureToggle();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {todayKey} = useHomeTime();

  const {t} = useTranslation();

  const dateKey = format(date, 'yyyy-MM-dd');

  const activeWorkday = useAppSelector((state) => getActiveWorkday(state, time));
  const workdays = useAppSelector(getWorkdaysForDate(dateKey));

  // Display currently active workday if any, otherwise default to first workday for the selected date
  const workday = activeWorkday ?? workdays?.data?.at(0);
  const buildingId = workday?.status === 'OfficeDay' ? workday.buildingId : null;

  // Check in
  const checkInAllowed = useAppSelector(checkInRelevant);

  const canShowCheckInTile =
    // Feature allowed
    checkInAllowed &&
    // Office day
    buildingId !== null &&
    // Is today
    dateKey === todayKey;

  const workdayLoading = !useAppSelector(getSomeWorkdaysHaveLoaded);
  const defaultBuilding = useAppSelector(getDefaultBuilding);

  const workspaceReservations = useAppSelector(getWorkspaceReservationsForDate(dateKey));
  const workspaceReservationLoading = workspaceReservations?.status === 'pending';

  const parkingReservation = useAppSelector((state) => getParkingReservationByDate(state, dateKey)) ?? null;
  const isLoadingCheckInStatus = useAppSelector(getIsLoadingUserCheckIn);
  const {eventType: checkInEventType, buildingId: checkInBuildingId} = useAppSelector(getUserCheckInStatus);
  const isOfficeDay = buildingId !== null;

  const checkInDisabled =
    checkInEventType === CheckInEventType.NotAtTheOffice && (!workday || workday.status !== 'OfficeDay');
  const isNotCheckedInYet = checkInEventType === CheckInEventType.NotCheckedInYet;
  const isNotAtTheOffice = checkInEventType === CheckInEventType.NotAtTheOffice;

  // There does not seem to be an "updating" state in the store, so for now we'll manage this in component.
  // Should be improved in the future
  const [requestedOnServer, setRequestedOnServer] = useState<CheckInEventType | null>(null);
  const isUpdatingCheckIn = requestedOnServer !== null && requestedOnServer !== checkInEventType;
  const CheckInButtonTitle = isNotCheckedInYet || isNotAtTheOffice ? t('workplace:CheckIn') : t('workplace:CheckOut');

  const topRowIcon: IconNames =
    workday?.status === 'OfficeDay'
      ? 'office'
      : workday?.status === 'NotWorking'
      ? 'calendarX'
      : workday?.status === 'WorkingRemotely'
      ? 'remote'
      : 'locationUnknown';

  const topRowHeader =
    workday?.status === 'OfficeDay'
      ? workday.buildingName
      : workday?.status === 'NotWorking'
      ? t('workplace:NotWorking')
      : workday?.status === 'WorkingRemotely'
      ? t('workplace:WorkingRemotely')
      : t('workplace:WhereWillYouBe');

  async function handleUpdateCheckInStatus(status: CheckInEventType) {
    setRequestedOnServer(status);

    const {success} = await dispatch(
      withAsyncThunkErrorHandling(() =>
        updateCheckInStatusNew({
          buildingId: status === CheckInEventType.AtTheOffice ? buildingId ?? undefined : checkInBuildingId,
          eventType: status,
          sourceType: CheckInSourceType.Manual,
        }),
      ),
    );
    if (success && enabledFeatures.CustomerBranding) {
      toast.success(
        status === CheckInEventType.AtTheOffice ? t('workplace:YouAreCheckedIn') : t('workplace:YouAreCheckedOut'),
      );
    }
  }

  function updateToNotAtTheOffice(e: MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    if (isLoadingCheckInStatus || checkInDisabled) return;
    trackEvent('Home_WorkdayTile__ChangeCheckInStatus', {
      from: checkInEventType,
      to: CheckInEventType.NotAtTheOffice,
    });

    handleUpdateCheckInStatus(CheckInEventType.NotAtTheOffice);
  }

  function updateToAtTheOffice(e: MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    if (isLoadingCheckInStatus || checkInDisabled) return;

    trackEvent('Home_WorkdayTile__ChangeCheckInStatus', {
      from: checkInEventType,
      to: CheckInEventType.AtTheOffice,
    });

    handleUpdateCheckInStatus(CheckInEventType.AtTheOffice);
  }

  const contentLoading = workdayLoading || workspaceReservationLoading;

  // The workday tile is technically "just another tile", but in reality it represents an important
  // first part of the home page. That's why once it's loaded we consider the home page to have meaningful
  // content and we track its performance.
  useEffect(() => {
    trackPageComponentedRendered('home', !contentLoading);
  }, [contentLoading]);

  const showCheckInButtonSimplified = canShowCheckInTile && workday?.status === 'OfficeDay';

  const handleTileClick = () => {
    navigate(`/calendar/${dateKey}`);
  };

  return contentLoading ? (
    <LoaderTile
      rows={[[48]]}
      withTitle={false}
    />
  ) : (
    <Tile>
      <FlexCol
        width="100%"
        {...(isOfficeDay ? {gap: 16} : {})}>
        <FlexRow
          alignItems="center"
          className="cursor-pointer"
          aria-label={t('ChangeLocationRow')}
          onClick={handleTileClick}>
          <FlexRow
            justifyContent="center"
            alignItems="center"
            gap={16}>
            <FlexCol
              justifyContent="center"
              alignItems="center"
              className=" bg-energizing-yellow-500* h-12 w-12 rounded-lg shrink-0">
              <Icon
                icon={topRowIcon}
                size="24px"
              />
            </FlexCol>

            <FlexCol>
              <H4>{topRowHeader}</H4>

              {enabledFeatures.TimeslotsUI && workday !== undefined ? (
                <Subtitle>{timeLabel(workday.localStart, workday.localEnd, t)}</Subtitle>
              ) : null}

              {!workday && defaultBuilding ? (
                <Subtitle>{t('YourDefaultOfficeIsShown', {officeName: defaultBuilding.name})}</Subtitle>
              ) : null}
            </FlexCol>
          </FlexRow>

          <FlexRow
            alignItems="center"
            gap={16}>
            {showCheckInButtonSimplified && (
              <Button
                disabled={isUpdatingCheckIn}
                button={checkInEventType === CheckInEventType.AtTheOffice ? 'secondary' : 'primary'}
                onClick={
                  checkInEventType === CheckInEventType.AtTheOffice ? updateToNotAtTheOffice : updateToAtTheOffice
                }
                className={cn('flex-1', {
                  'hidden md:flex': enabledFeatures.TimeslotsUI,
                })}>
                {CheckInButtonTitle}
              </Button>
            )}

            <FlexRow gap={16}>
              {workspaceReservations?.data?.length ? (
                <Icon
                  icon="desk"
                  size="24px"
                />
              ) : null}
              {parkingReservation && isOfficeDay ? (
                <Icon
                  icon="parking"
                  size="24px"
                />
              ) : null}
            </FlexRow>

            <Icon
              icon={'caretRight'}
              size="24px"
            />
          </FlexRow>
        </FlexRow>

        {enabledFeatures.TimeslotsUI && showCheckInButtonSimplified && (
          <Button
            disabled={isUpdatingCheckIn}
            button={checkInEventType === CheckInEventType.AtTheOffice ? 'secondary' : 'primary'}
            onClick={checkInEventType === CheckInEventType.AtTheOffice ? updateToNotAtTheOffice : updateToAtTheOffice}
            className="flex-1 md:hidden">
            {CheckInButtonTitle}
          </Button>
        )}
      </FlexCol>
    </Tile>
  );
};
